import React from 'react';

import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';

import { Dependencies } from 'utils/permissions';

const styles = theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    group: {
        width: '50%',
        padding: '16px 24px',
    },
    groupHeader: {
        fontWeight: 500,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& > h3': {
            marginTop: 18,
            fontWeight: 500,
        },
    },
    groupOptions: {
        display: 'flex',
    },
    groupOption: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 50,
    },
    rule: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    ruleLabel: {
        color: theme.palette.text.secondary,
        fontSize: '0.938rem',
    },
    ruleOptions: {
        display: 'flex',
    },
    ruleOption: {
        display: 'flex',
        justifyContent: 'center',
        width: 50,
    },
    checkbox: {
        padding: 4,
    },
    smallerFont: {
        fontSize: '0.938rem',
    },
});

const AccessForm = ({ classes, children }) => (
    <div className={classes.root}>{children}</div>
);

const VIEW_NAME = 'view';
const CHANGE_NAME = 'change';

const VIEW_TYPE = 1;
const CHANGE_TYPE = 2;
const ALL_TYPE = 3;

const NAME_TO_TYPE = {
    [VIEW_NAME]: VIEW_TYPE,
    [CHANGE_NAME]: ALL_TYPE,
};
const TYPE_TO_NAME = {
    [VIEW_TYPE]: VIEW_NAME,
    [ALL_TYPE]: CHANGE_NAME,
};

function initValues(options, values) {
    return options.reduce((acc, { name }) => {
        acc[name] = NAME_TO_TYPE[values[name]] || 0;
        return acc;
    }, {});
}

function prepareWithDependencies(
    rules,
    name,
    type,
    enabled,
    config = Dependencies
) {
    const typeName = TYPE_TO_NAME[type];
    const newRules = {};
    if (!enabled) {
        newRules[name] = '';
        for (const item of config) {
            for (const require of item.requires) {
                if (require.permission === name && require.type === typeName) {
                    newRules[item.permission] = '';
                    break;
                }
            }
        }
    } else {
        newRules[name] = typeName;
        for (const item of config) {
            const isEqualType =
                item.type === typeName ||
                (item.type === 'view' && typeName === 'change');
            if (item.permission === name && isEqualType) {
                for (const require of item.requires) {
                    const currentRule = rules[require.permission];
                    if (currentRule !== 'change') {
                        newRules[require.permission] = require.type;
                    }
                }
            }
        }
    }
    return { ...rules, ...newRules };
}

AccessForm.Group = withStyles(styles)(
    ({ disabled, classes, title, rules, options, onChange }) => {
        const values = initValues(options, rules);
        const toggleAll = type => (e, enabled) => {
            const newRoles = options.reduce((acc, option) => {
                acc[option.name] = enabled ? TYPE_TO_NAME[type] : '';
                return acc;
            }, {});
            onChange(newRoles);
        };
        const onChangeRule = (name, type) => (e, enabled) =>
            onChange(prepareWithDependencies(rules, name, type, enabled));

        const isAllSelected = type => {
            const names = Object.keys(values);
            if (names.length) {
                return names.every(name => !!(values[name] & type));
            }
            return false;
        };
        return (
            <div className={classes.group}>
                <div className={classes.groupHeader}>
                    <Typography component="h3" className={classes.groupTitle}>
                        {title}
                    </Typography>
                    <div className={classes.groupOptions}>
                        <div className={classes.groupOption}>
                            <Typography
                                component="span"
                                className={classes.smallerFont}
                            >
                                View
                            </Typography>
                            <Checkbox
                                disabled={disabled}
                                className={classes.checkbox}
                                checked={isAllSelected(VIEW_TYPE)}
                                onChange={toggleAll(VIEW_TYPE)}
                            />
                        </div>
                        <div className={classes.groupOption}>
                            <Typography
                                component="span"
                                className={classes.smallerFont}
                            >
                                Change
                            </Typography>
                            <Checkbox
                                disabled={disabled}
                                className={classes.checkbox}
                                checked={isAllSelected(CHANGE_TYPE)}
                                onChange={toggleAll(ALL_TYPE)}
                            />
                        </div>
                    </div>
                </div>
                <div className={classes.groupBody}>
                    {options.map(option => (
                        <Rule
                            disabled={disabled}
                            key={option.name}
                            option={option}
                            value={values[option.name]}
                            onChange={onChangeRule}
                        />
                    ))}
                </div>
            </div>
        );
    }
);

const Rule = withStyles(styles)(
    ({ disabled, classes, option, value, onChange }) => (
        <div className={classes.rule}>
            <Typography component="div" className={classes.ruleLabel}>
                {option.value}
            </Typography>
            <div className={classes.ruleOptions}>
                <div className={classes.ruleOption}>
                    <Checkbox
                        disabled={disabled}
                        className={classes.checkbox}
                        checked={!!(value & VIEW_TYPE)}
                        onChange={onChange(option.name, VIEW_TYPE)}
                    />
                </div>
                <div className={classes.ruleOption}>
                    <Checkbox
                        disabled={disabled}
                        className={classes.checkbox}
                        checked={!!(value & CHANGE_TYPE)}
                        onChange={onChange(option.name, ALL_TYPE)}
                    />
                </div>
            </div>
        </div>
    )
);

export default withStyles(styles)(AccessForm);
